import { motion } from 'framer-motion';
import React from 'react';
import { FiCalendar, FiDollarSign, FiFileText, FiShield, FiUser } from 'react-icons/fi';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';

const TermsOfService = () => {
  const lastUpdated = "December 15, 2024";

  const sections = [
    {
      title: "Acceptance of Terms",
      icon: FiFileText,
      content: [
        {
          subtitle: "Agreement to Terms",
          text: "By accessing and using Innofusion Analytics' website and services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service."
        },
        {
          subtitle: "Modifications",
          text: "We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting on our website. Your continued use of our services after any such changes constitutes your acceptance of the new terms."
        }
      ]
    },
    {
      title: "Description of Services",
      icon: FiUser,
      content: [
        {
          subtitle: "IT Staffing and Consulting",
          text: "Innofusion Analytics provides IT staffing, consulting, and technology solutions to businesses. Our services include but are not limited to talent acquisition, project management, software development, and strategic technology consulting."
        },
        {
          subtitle: "Service Availability",
          text: "While we strive to provide uninterrupted access to our services, we do not guarantee that our services will be available at all times. We may suspend or restrict access to our services for maintenance, updates, or other operational reasons."
        }
      ]
    },
    {
      title: "User Responsibilities",
      icon: FiShield,
      content: [
        {
          subtitle: "Account Security",
          text: "You are responsible for maintaining the confidentiality of your account information and password. You agree to accept responsibility for all activities that occur under your account or password."
        },
        {
          subtitle: "Prohibited Uses",
          text: "You may not use our services for any illegal or unauthorized purpose. You must not, in the use of our services, violate any laws in your jurisdiction including but not limited to copyright laws."
        },
        {
          subtitle: "Content Standards",
          text: "Any content you submit through our services must be accurate, complete, and not misleading. You agree not to submit any content that is defamatory, abusive, harassing, threatening, or otherwise objectionable."
        }
      ]
    },
    {
      title: "Intellectual Property",
      icon: FiFileText,
      content: [
        {
          subtitle: "Our Intellectual Property",
          text: "The service and its original content, features, and functionality are and will remain the exclusive property of Innofusion Analytics and its licensors. The service is protected by copyright, trademark, and other laws."
        },
        {
          subtitle: "User Content",
          text: "You retain ownership of any intellectual property rights that you hold in content that you submit to our service. However, by submitting content, you grant us a worldwide, non-exclusive, royalty-free license to use, modify, and distribute such content."
        },
        {
          subtitle: "Respect for Third-Party Rights",
          text: "You agree not to infringe upon the intellectual property rights of third parties. If you believe that your work has been copied in a way that constitutes copyright infringement, please contact us immediately."
        }
      ]
    },
    {
      title: "Payment Terms",
      icon: FiDollarSign,
      content: [
        {
          subtitle: "Service Fees",
          text: "Fees for our services are as described in your service agreement or as otherwise communicated to you. All fees are non-refundable unless otherwise specified in your individual service agreement."
        },
        {
          subtitle: "Payment Methods",
          text: "We accept various payment methods as specified during the service engagement process. Payment terms will be clearly outlined in your service agreement."
        },
        {
          subtitle: "Late Payments",
          text: "Late payments may be subject to additional fees or service suspension as outlined in your individual service agreement."
        }
      ]
    },
    {
      title: "Privacy and Data Protection",
      icon: FiShield,
      content: [
        {
          subtitle: "Privacy Policy",
          text: "Your privacy is important to us. Please review our Privacy Policy, which also governs your use of our services, to understand our practices."
        },
        {
          subtitle: "Data Security",
          text: "We implement appropriate technical and organizational measures to protect your personal data against unauthorized or unlawful processing and against accidental loss, destruction, or damage."
        }
      ]
    },
    {
      title: "Limitation of Liability",
      icon: FiDollarSign,
      content: [
        {
          subtitle: "Service Disclaimer",
          text: "Our services are provided on an 'as is' and 'as available' basis. We make no representations or warranties of any kind, express or implied, as to the operation of our services or the information, content, materials, or products included therein."
        },
        {
          subtitle: "Limitation of Damages",
          text: "In no event shall Innofusion Analytics, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses."
        }
      ]
    },
    {
      title: "Termination",
      icon: FiUser,
      content: [
        {
          subtitle: "Termination by Either Party",
          text: "Either party may terminate the service relationship at any time by providing written notice as specified in the individual service agreement."
        },
        {
          subtitle: "Effect of Termination",
          text: "Upon termination, your right to use our services will cease immediately. All provisions of these terms which by their nature should survive termination shall survive termination."
        }
      ]
    },
    {
      title: "Governing Law and Jurisdiction",
      icon: FiDollarSign,
      content: [
        {
          subtitle: "Applicable Law",
          text: "These terms shall be interpreted and governed by the laws of the jurisdiction in which Innofusion Analytics is incorporated, without regard to its conflict of law provisions."
        },
        {
          subtitle: "Dispute Resolution",
          text: "Any disputes arising out of or relating to these terms or our services shall be resolved through binding arbitration in accordance with the commercial arbitration rules."
        }
      ]
    }
  ];

  const contactInfo = {
    email: "Connect@innofusionanalytics.com",
    phone: "+1 (657) 867 7883",
    address: "United States"
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4 font-accent tracking-wide">
              Legal Terms
            </span>
            <h1 className="text-5xl md:text-6xl font-bold leading-tight font-display tracking-tight text-gray-900 mb-6">
              Terms of
              <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
                Service
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              These Terms of Service govern your use of Innofusion Analytics' website and services. 
              Please read these terms carefully before using our services.
            </p>
            <div className="flex items-center justify-center space-x-2 text-gray-500">
              <FiCalendar size={16} />
              <span className="text-sm font-body">Last updated: {lastUpdated}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                    <section.icon className="text-white" size={24} />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 font-display tracking-tight">
                    {section.title}
                  </h2>
                </div>
                
                <div className="space-y-6">
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex}>
                      {item.subtitle && (
                        <h3 className="text-xl font-semibold text-gray-900 mb-3 font-heading tracking-wide">
                          {item.subtitle}
                        </h3>
                      )}
                      <p className="text-gray-600 font-body leading-relaxed">
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-8 border border-blue-100"
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 font-display tracking-tight">
                Legal Contact Information
              </h2>
              <p className="text-xl text-gray-600 font-body font-light tracking-wide leading-relaxed">
                If you have any questions about these Terms of Service, please contact us:
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-white rounded-2xl border border-blue-100">
                <FiDollarSign className="w-8 h-8 text-blue-600 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2 font-heading tracking-wide">Email</h3>
                <p className="text-gray-600 font-body">{contactInfo.email}</p>
              </div>
              
              <div className="text-center p-6 bg-white rounded-2xl border border-blue-100">
                <FiDollarSign className="w-8 h-8 text-blue-600 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2 font-heading tracking-wide">Phone</h3>
                <p className="text-gray-600 font-body">{contactInfo.phone}</p>
              </div>
              
              <div className="text-center p-6 bg-white rounded-2xl border border-blue-100">
                <FiDollarSign className="w-8 h-8 text-blue-600 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2 font-heading tracking-wide">Address</h3>
                <p className="text-gray-600 font-body text-sm">{contactInfo.address}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Agreement Notice */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-white/5"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-display tracking-tight">
              Your Agreement
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfService; 